import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import debounce from 'lodash/debounce';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@crono-one/design-system';
import { cn } from 'utils/style-utils';
import { TeamBadge } from './TeamBadge';

const OVERFLOW_BADGE_WIDTH = 36; // approx width of "+N" button

const sizeConfig = {
  md: {
    containerClassName: 'gap-1.5',
  },
  sm: {
    containerClassName: 'gap-1',
  },
};
interface TeamsBadgeListProps {
  teams: {
    id: number;
    name: string;
  }[];
  selectedTeamId?: number;
  collisionBoundary?: Element | null;
  size?: 'sm' | 'md';
  alignEnd?: boolean;
}

export const TeamsBadgeList = ({
  teams,
  selectedTeamId,
  collisionBoundary,
  alignEnd,
  size = 'md',
}: TeamsBadgeListProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const badgeRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [visibleCount, setVisibleCount] = useState(teams.length);

  const [open, setOpen] = useState(false);
  const [debouncedOpen, setDebouncedOpen] = useState(false);

  const debouncedSetOpen = useMemo(
    () => debounce((value: boolean) => setDebouncedOpen(value), 100),
    [],
  );

  useEffect(() => {
    debouncedSetOpen(open);
    return () => debouncedSetOpen.cancel();
  }, [open, debouncedSetOpen]);

  const handleMouseEnter = () => setOpen(true);
  const handleMouseLeave = () => setOpen(false);

  const sortedTeams = teams.toSorted((a, b) => {
    if (a.id === selectedTeamId) return -1;
    if (b.id === selectedTeamId) return 1;
    return a.name.localeCompare(b.name);
  });

  const gapPx = size === 'sm' ? 4 : 6;

  const recalculate = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const containerWidth = container.clientWidth;
    const widths = badgeRefs.current
      .slice(0, sortedTeams.length)
      .map((el) => el?.offsetWidth ?? 0);

    let used = 0;
    let count = 0;

    for (let i = 0; i < widths.length; i++) {
      const gap = i > 0 ? gapPx : 0;
      const overflowSpace =
        i < widths.length - 1 ? gapPx + OVERFLOW_BADGE_WIDTH : 0;

      if (used + gap + widths[i] + overflowSpace <= containerWidth) {
        used += gap + widths[i];
        count++;
      } else {
        break;
      }
    }

    setVisibleCount(count);
  }, [sortedTeams.length, gapPx]);

  // Recalculate after DOM paint when teams change (before browser paint = no flicker)
  useLayoutEffect(() => {
    recalculate();
  }, [recalculate, teams]);

  // Recalculate on container resize (handles both window resize and chat panel toggle)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(recalculate);
    observer.observe(container);
    return () => observer.disconnect();
  }, [recalculate]);

  const hiddenTeams = sortedTeams.slice(visibleCount);

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex items-center overflow-hidden min-w-0',
        sizeConfig[size].containerClassName,
        { 'justify-end': !!alignEnd },
      )}
    >
      {sortedTeams.length > 0 ? (
        sortedTeams.map((team, i) => (
          <TeamBadge
            key={team.id}
            team={team}
            size={size}
            ref={(el) => {
              badgeRefs.current[i] = el;
            }}
            className={cn({
              'absolute invisible pointer-events-none': i >= visibleCount,
            })}
          />
        ))
      ) : (
        <div className="text-xs font-medium text-neutral-500 flex items-center justify-center py-1 px-2 rounded-md border border-neutral-500">
          No teams
        </div>
      )}

      {hiddenTeams.length > 0 && (
        <Popover open={debouncedOpen} onOpenChange={setOpen}>
          <PopoverTrigger
            asChild
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            <Button
              className="shrink-0 py-1 px-2 h-6.5 text-xs focus-visible:ring-0"
              size="xs"
              variant="light"
              tone="gray"
              shape="rounded"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              +{hiddenTeams.length}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className="z-99999999 max-w-60 w-auto"
            collisionBoundary={collisionBoundary}
            hideWhenDetached
          >
            <div className="flex flex-col gap-1.5">
              {hiddenTeams.map((team) => (
                <TeamBadge
                  key={team.id}
                  team={team}
                  size={size}
                  className="max-w-fit"
                  hideTooltip
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};
